/**
 * Report-an-issue control (R-issues).
 *
 * A small, always-available affordance: a "Report an issue" button that reveals
 * a short form (a textarea + submit). On submit it calls the injected
 * {@link IssueReporterProps.onReport} with the player's text; the PARENT decides
 * where it goes — in the play view it is wired to the campaign-anchored report
 * (`POST /campaigns/{id}/issues`, which the server stamps with the current
 * conversation-log position), and elsewhere to the general report (`POST
 * /issues`). The component itself is campaign-agnostic — it just collects text
 * and reports success/failure — so it is reusable and unit-testable with a fake
 * `onReport`, mirroring the DiceRoller's callback-prop shape.
 */

import { useState } from "react";

export interface IssueReporterProps {
  /**
   * Submit the report. Resolves on success (the control shows a thank-you and
   * resets); rejects with a player-safe Error on failure (shown inline).
   */
  readonly onReport: (text: string) => Promise<void>;
  /**
   * Optional label hint shown under the field, e.g. "This report will be linked
   * to your current campaign." so the player knows a campaign report is
   * anchored. Omit for a general report.
   */
  readonly contextHint?: string;
}

type Phase = "idle" | "open" | "sent";

export function IssueReporter({ onReport, contextHint }: IssueReporterProps) {
  const [phase, setPhase] = useState<Phase>("idle");
  const [text, setText] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = text.trim();
    if (trimmed.length === 0) {
      setError("Describe the issue before sending.");
      return;
    }
    setError(null);
    setBusy(true);
    try {
      await onReport(trimmed);
      setText("");
      setPhase("sent");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't send that report.");
    } finally {
      setBusy(false);
    }
  };

  if (phase === "idle" || phase === "sent") {
    return (
      <section className="issue-reporter" aria-label="Report an issue">
        {phase === "sent" ? (
          <p className="issue-sent" role="status" data-testid="issue-sent">
            Thanks — your report was sent.
          </p>
        ) : null}
        <button
          type="button"
          className="report-issue-open"
          onClick={() => {
            setPhase("open");
            setError(null);
          }}
        >
          Report an issue
        </button>
      </section>
    );
  }

  return (
    <section className="issue-reporter" aria-label="Report an issue">
      <form onSubmit={submit}>
        <label htmlFor="issue-text">Report an issue</label>
        <textarea
          id="issue-text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="What went wrong? (e.g. 'the DM forgot my gold after the fight')"
          rows={3}
          maxLength={4000}
          disabled={busy}
        />
        {contextHint ? <p className="issue-context-hint">{contextHint}</p> : null}
        <div className="issue-actions">
          <button type="submit" disabled={busy || text.trim() === ""}>
            Send report
          </button>
          <button
            type="button"
            className="issue-cancel"
            disabled={busy}
            onClick={() => {
              setPhase("idle");
              setText("");
              setError(null);
            }}
          >
            Cancel
          </button>
        </div>
        {error ? (
          <p className="issue-error" role="alert">
            {error}
          </p>
        ) : null}
      </form>
    </section>
  );
}
